import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {DataService} from './data.service';

export interface FixedAssetConfigDto {
  id?: string;
  defaultDepreciationMethod: string;
  defaultUsefulLifeMonths?: number;
  defaultDepreciationRate?: number;
  prorateConvention: string; // Monthly | Quarterly | BiAnnual | Annual
  monthlyProrate: boolean;
  assetTagPattern?: string;
  defaultAssetAccountId?: string;
  defaultAccumulatedDepreciationAccountId?: string;
  defaultDepreciationExpenseAccountId?: string;
  defaultGainOnDisposalAccountId?: string;
  defaultLossOnDisposalAccountId?: string;
  // New: Default Proceeds Suspense Account id
  defaultProceedsSuspenseAccountId?: string;
}

@Injectable({providedIn: 'root'})
export class FixedAssetsConfigService {
  private controller = 'fixed-assets/config';
  private warnedOnce = false;
  private lastId: string | undefined;

  constructor(private api: DataService) {
  }

  get(): Observable<FixedAssetConfigDto> {
    // API returns bare JSON (no { Data: ... } wrapper)
    return this.api.getData(this.controller).pipe(
      tap((res) => {
        // try {
        //   console.log('[FixedAssetConfig API][GET]', res);
        // } catch {
        // }
      }),
      map((res: any) => this.normalizeDto(res)),
      tap((dto) => {
        this.lastId = dto?.id || this.lastId;
        // try {
        //   console.log('[FixedAssetConfig API][GET][normalized id]', this.lastId);
        // } catch {
        // }
      }),
      catchError((err) => {
        this.warnOnce('FA Config API unavailable; please check backend.');
        // try {
        //   console.log('[FixedAssetConfig API][GET][error]', err);
        // } catch {
        // }
        throw err;
      })
    );
  }

  upsert(dto: FixedAssetConfigDto): Observable<FixedAssetConfigDto> {
    // API returns saved DTO (bare)
    // Preserve existing id across save: if dto.id exists but API omits/changes Id,
    // re-attach the original id so the UI retains the fetched identity
    // Per rule: send the id we were given on fetch, or omit id entirely for new
    // Important: always send the id property to match API model shape.
    // For existing records, send the exact id we fetched.
    // For NEW records (no existing id), send a ZERO-FILLED GUID.
    const sanitized: any = {...dto};
    // If this looks like an existing record but id is missing on the payload,
    // use the last fetched id; only use zero-Guid when truly new (no lastId and no dto.id)
    if (sanitized.id) {
      // keep as-is
    } else if (this.lastId) {
      sanitized.id = this.lastId;
    } else {
      sanitized.id = '00000000-0000-0000-0000-000000000000';
    }
    const apiPayload = this.toApiDto(sanitized);
    return this.api.post(this.controller, apiPayload).pipe(
      tap(() => {
        try {
          // console.log('[FixedAssetConfig API][POST] payload', sanitized);
        } catch {
        }
      }),
      tap((res) => {
        // try {
        //   console.log('[FixedAssetConfig API][POST] response', res);
        // } catch {
        // }
      }),
      map((res: any) => {
        const normalized = this.normalizeDto(res);
        // If we had an inbound id and the response didn’t include one, keep the original
        if ((dto?.id || this.lastId) && !normalized?.id) {
          normalized.id = dto?.id || this.lastId as string;
        }
        // update lastId from response
        this.lastId = normalized?.id || this.lastId;
        return normalized;
      }),
      catchError((err) => {
        this.warnOnce('FA Config API save failed; please check backend.');
        // try {
        //   console.log('[FixedAssetConfig API][POST][error]', err);
        // } catch {
        // }
        throw err;
      })
    );
  }

  private warnOnce(msg: string) {
    if (!this.warnedOnce) {
      this.warnedOnce = true;
      console.warn(msg);
    }
  }

  // Normalize API (PascalCase) to FE (camelCase) expectations
  private normalizeDto(api: any): FixedAssetConfigDto {
    if (!api || typeof api !== 'object') {
      return api as FixedAssetConfigDto;
    }
    const dto: FixedAssetConfigDto = {
      id: api.Id ?? api.id,
      defaultDepreciationMethod: api.DefaultDepreciationMethod ?? api.defaultDepreciationMethod,
      defaultUsefulLifeMonths: api.DefaultUsefulLifeMonths ?? api.defaultUsefulLifeMonths,
      defaultDepreciationRate: api.DefaultDepreciationRate ?? api.defaultDepreciationRate,
      prorateConvention: api.ProrateConvention ?? api.prorateConvention,
      monthlyProrate: api.MonthlyProrate ?? api.monthlyProrate,
      assetTagPattern: api.AssetTagPattern ?? api.assetTagPattern,
      defaultAssetAccountId: api.DefaultAssetAccountId ?? api.defaultAssetAccountId,
      defaultAccumulatedDepreciationAccountId: api.DefaultAccumulatedDepreciationAccountId ?? api.defaultAccumulatedDepreciationAccountId,
      defaultDepreciationExpenseAccountId: api.DefaultDepreciationExpenseAccountId ?? api.defaultDepreciationExpenseAccountId,
      defaultGainOnDisposalAccountId: api.DefaultGainOnDisposalAccountId ?? api.defaultGainOnDisposalAccountId,
      defaultLossOnDisposalAccountId: api.DefaultLossOnDisposalAccountId ?? api.defaultLossOnDisposalAccountId,
      defaultProceedsSuspenseAccountId: api.DefaultProceedsSuspenseAccountId ?? api.defaultProceedsSuspenseAccountId,
    };
    // try {
    //   console.log('[FixedAssetConfig API][normalize]', dto);
    // } catch {
    // }
    return dto;
  }

  // Map FE DTO (camelCase) to API DTO (PascalCase)
  private toApiDto(dto: FixedAssetConfigDto): any {
    if (!dto || typeof dto !== 'object') {
      return dto;
    }
    return {
      Id: dto.id,
      DefaultDepreciationMethod: dto.defaultDepreciationMethod,
      DefaultUsefulLifeMonths: dto.defaultUsefulLifeMonths,
      DefaultDepreciationRate: dto.defaultDepreciationRate,
      ProrateConvention: dto.prorateConvention,
      MonthlyProrate: dto.monthlyProrate,
      AssetTagPattern: dto.assetTagPattern,
      DefaultAssetAccountId: dto.defaultAssetAccountId,
      DefaultAccumulatedDepreciationAccountId: dto.defaultAccumulatedDepreciationAccountId,
      DefaultDepreciationExpenseAccountId: dto.defaultDepreciationExpenseAccountId,
      DefaultGainOnDisposalAccountId: dto.defaultGainOnDisposalAccountId,
      DefaultLossOnDisposalAccountId: dto.defaultLossOnDisposalAccountId,
      DefaultProceedsSuspenseAccountId: dto.defaultProceedsSuspenseAccountId,
    };
  }
}
